/**
 * Tour guiado: shell (header/menu) + passos por rota.
 * Alvos com seletor inválido ou ausente no DOM são removidos em filterStepsByExistingTargets.
 */

export function filterStepsByExistingTargets(steps) {
  if (!Array.isArray(steps)) return [];
  return steps.filter((step) => {
    const t = step?.target;
    if (t == null || t === "body") return true;
    if (typeof t !== "string") return true;
    try {
      return Boolean(document.querySelector(t));
    } catch {
      return false;
    }
  });
}

export function resolveTourRouteKey(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";

  if (p === "/" || p.startsWith("/chat")) return "chat";
  if (p.startsWith("/dashboard")) return "dashboard";
  if (p === "/clients") return "clients";
  if (p.startsWith("/clients/")) return "client-detail";
  if (p === "/pipeline" || p.startsWith("/pipeline/")) return "pipeline";
  if (p.startsWith("/sales-pipeline")) return "sales-pipeline";
  if (p === "/products" || p.startsWith("/products/")) return "products";
  if (p.startsWith("/settings/")) return "settings";
  if (p.startsWith("/agents")) {
    if (/\/analytics$/.test(p)) return "agent-analytics";
    if (p === "/agents" || p === "/agents/") return "agents";
    return "agent-form";
  }
  if (p.startsWith("/search-results")) return "search-results";
  if (p === "/profile") return "profile";

  const catalogPrefixes = [
    "/categories",
    "/coupons",
    "/banners",
    "/contents",
    "/pipeline-stages",
    "/awareness-stages",
    "/surveys",
    "/campaigns",
    "/user-details",
    "/user-plan",
  ];
  if (catalogPrefixes.some((pre) => p === pre || p.startsWith(`${pre}/`))) return "catalog";

  return "generic";
}

const SETTINGS_SEGMENT_LABELS = {
  users: "Usuários",
  channels: "Canais",
  companies: "Empresas e projetos",
  types: "Tipos de solicitação",
  integrations: "Integrações",
  "automation-rules": "Automação",
  "payment-methods": "Formas de pagamento",
  "delivery-methods": "Entregas",
  "antifraud-methods": "Antifraude",
  positions: "Cargos",
  chatbot: "ChatBots",
};

function settingsTitleFromPath(pathname) {
  const seg = pathname.replace(/^\/settings\//, "").split("/")[0] || "";
  return SETTINGS_SEGMENT_LABELS[seg] || "Configurações";
}

export function buildShellTourBundles(isMobile, options = {}) {
  const { omitMainIntro = false } = options;
  const welcome = {
    target: "body",
    placement: "center",
    disableBeacon: true,
    content:
      "Bem-vindo ao tour guiado. Primeiro a navegação global; em seguida, os elementos da tela em que você está. Use Próximo ou Pular — reabra em Ajuda e Treinamento no menu do avatar (ícone da foto).",
  };

  const logo = {
    target: ".tour-logo",
    content:
      "Marca e início: clique no logo para voltar ao painel principal (home). Útil quando você está em uma tela profunda e quer recomeçar pela visão geral.",
  };

  const menu = {
    target: ".tour-menu",
    content: isMobile
      ? "Menu (três linhas): abre a gaveta com todos os módulos — Dashboard, Atendimento, Pipeline, Clientes, Produtos e Configurações (conforme permissão). Toque fora ou no ícone de novo para fechar."
      : "Menu lateral: recolhe ou expande a coluna da esquerda. Recolhido, sobra mais espaço para listas e chat; expandido, você lê o nome completo de cada módulo.",
  };

  const sidebarNav = {
    target: ".tour-sidebar-nav",
    content:
      "Menu lateral: na parte superior estão os módulos operacionais — Dashboard (indicadores), Atendimento (conversas em todos os canais), Pipeline de Vendas, Clientes, Níveis de consciência e Produtos. Abaixo, quando sua permissão permitir, aparece Configurações: empresas (projetos), usuários, cargos, ChatBots, agentes de IA, canais, integrações, tipos, categorias, automação, antifraude, pagamentos, entrega, etapas de consciência, cupons, banners e conteúdos. Só o que você pode acessar é exibido.",
  };

  const mainContent = {
    target: ".tour-main-content",
    content:
      "Área de trabalho: cada item do menu abre aqui — dashboards, listas, formulários, conversas em tempo real e relatórios. A seguir, destacamos os blocos específicos desta página.",
  };

  const mainContentMobile = {
    target: ".tour-main-content",
    content:
      "Área principal: no celular ou tablet o menu fica no ícone de três linhas. Abaixo, os elementos específicos desta tela. Role para ver listas longas ou o fim do formulário.",
  };

  const mobileSearchCompany = {
    target: ".tour-mobile-search-company",
    content:
      "Busca e empresa: este ícone abre uma tela com busca de clientes e seleção de empresa/projeto. Métricas, atendimento e vários cadastros consideram o projeto ativo — vale conferir antes de responder ou alterar dados.",
  };

  const search = {
    target: ".tour-search",
    content:
      "Busca global: digite para localizar clientes, conversas ou registros ligados ao contexto atual. Quanto mais específico o termo, mais rápido você chega ao que precisa.",
  };

  const company = {
    target: ".tour-company",
    content:
      "Empresa / projeto ativo: define em qual unidade ou base você está operando. Atendimento, indicadores e parte dos cadastros filtram por esse contexto — troque aqui quando mudar de filial ou marca.",
  };

  const theme = {
    target: ".tour-theme",
    content:
      "Tema claro ou escuro: escolha o que for mais confortável para o seu ambiente e para leitura prolongada em telas de atendimento.",
  };

  const ai = {
    target: ".tour-ai",
    content:
      "Assistente da plataforma (IA): pergunte sobre indicadores e visão agregada do workspace. Serve como apoio à leitura dos dados — confira sempre as telas e relatórios oficiais para decisões críticas.",
  };

  const profile = {
    target: ".tour-profile",
    content:
      "Menu do avatar: clique na foto. Lá estão Meu Perfil, Ajuda e Treinamento — único atalho de ajuda na barra: hub com materiais do workspace, IA por treinamento quando houver e tour guiado (sem materiais publicados, em alguns fluxos o tour abre direto) — e Sair. Quem administra o workspace publica treinamentos e define visibilidade.",
  };

  const head = [welcome, logo, menu];
  if (!isMobile) {
    head.push(sidebarNav);
    if (!omitMainIntro) head.push(mainContent);
  } else {
    if (!omitMainIntro) head.push(mainContentMobile);
    head.push(mobileSearchCompany);
  }

  const mid = !isMobile ? [search, company] : [];
  const tail = [theme, ai, profile];

  return { head, mid, tail };
}

export function mergeShellAndPageSteps({ head, mid, tail }, pageSteps) {
  const page = Array.isArray(pageSteps) ? pageSteps : [];
  return [...head, ...page, ...mid, ...tail];
}

function chatPageSteps(isMobile) {
  const layout = [
    {
      target: ".tour-chat-layout",
      placement: "top",
      content:
        "Atendimento em colunas: à esquerda, a fila de solicitações; no centro, o histórico de mensagens da conversa selecionada; à direita (quando aberto), detalhes do cliente, tags e insights de IA. No celular, cada parte vira uma tela — use voltar e os ícones de menu para alternar.",
    },
    {
      target: ".tour-chat-list-pane",
      placement: "right",
      content:
        "Painel da lista: agrupa suas solicitações (protocolos). No desktop, os botões acima fecham o painel, atualizam a lista e abrem nova solicitação.",
    },
    {
      target: ".tour-chat-list-toolbar",
      placement: "bottom",
      content: "Atalhos da lista: atualizar para puxar itens novos da fila; nova solicitação para iniciar um atendimento com cliente já cadastrado.",
    },
    {
      target: ".tour-chat-list-filters",
      placement: "bottom",
      content:
        "Busca e filtros da fila: refine por texto, use Meu dia para ver o que é seu, e o ícone de filtros para status, responsável e não lidas. Os chips mostram o que está ativo.",
    },
    {
      target: ".tour-chat-message-pane",
      placement: "left",
      content:
        "Área da conversa: exibe mensagens, mídias e o campo para responder. Sem conversa selecionada, aparece um resumo vazio até você escolher um protocolo na lista.",
    },
    {
      target: ".tour-chat-message-header",
      placement: "bottom",
      content:
        "Cabeçalho do atendimento: identidade do cliente, protocolo, canal, atribuição (fila, usuário ou agente de IA), status da solicitação e atalhos para popup ou painel de detalhes.",
    },
    {
      target: ".tour-chat-details-pane",
      placement: "left",
      content:
        "Detalhes e contexto: ficha do cliente, histórico complementar e ferramentas como insights de IA quando disponíveis. Feche com X para ganhar espaço na conversa.",
    },
  ];

  const empty = [
    {
      target: ".tour-chat-empty",
      placement: "top",
      content:
        "Primeiro acesso ao atendimento: quando ainda não há solicitações no projeto, esta área explica os canais e convida a criar o primeiro atendimento ou conectar canais em Configurações.",
    },
    {
      target: ".tour-chat-empty-cta",
      placement: "top",
      content:
        "Criar primeiro atendimento: inicia o fluxo de nova solicitação (é preciso escolher ou cadastrar um cliente). O link Conectar canais leva às integrações.",
    },
  ];

  return [...layout, ...empty];
}

function dashboardPageSteps() {
  return [
    {
      target: ".tour-dashboard-root",
      placement: "top",
      content:
        "Dashboard: visão de indicadores do workspace no período escolhido. Os números respeitam a empresa/projeto selecionado na barra superior.",
    },
    {
      target: ".tour-dashboard-period",
      placement: "left",
      content:
        "Período e exportação: defina data inicial e final para recalcular gráficos e tabelas. Em telas largas, Exportar gera CSV da aba atual.",
    },
    {
      target: ".tour-dashboard-tabs",
      placement: "top",
      content:
        "Abas de métricas: Visão Geral (atendimento), Clientes, Produtos e Vendas — cada uma carrega um conjunto de cartões e gráficos para o mesmo intervalo de datas.",
    },
    {
      target: ".tour-dashboard-tab-panel",
      placement: "top",
      content:
        "Conteúdo da aba: indicadores detalhados da dimensão selecionada. Role para ver todos os blocos.",
    },
  ];
}

function clientsPageSteps() {
  return [
    {
      target: ".tour-clients-root",
      placement: "top",
      content:
        "Clientes: cadastro central de pessoas e empresas que interagem com o workspace. A listagem considera busca e paginação.",
    },
    {
      target: ".tour-clients-header",
      placement: "bottom",
      content:
        "Título e ação principal: Adicionar Cliente abre o formulário inclusão. Edição costuma partir da própria tabela.",
    },
    {
      target: ".tour-clients-table",
      placement: "top",
      content:
        "Tabela: busque, ordene, altere página e use ações por linha (editar, excluir, iniciar conversa) conforme sua permissão.",
    },
  ];
}

function clientDetailPageSteps() {
  return [
    {
      target: ".tour-client-detail-root",
      placement: "top",
      content:
        "Ficha do cliente: visão 360° com resumo, indicadores e abas de histórico. Use Voltar para retornar à lista.",
    },
    {
      target: ".tour-client-detail-header",
      placement: "bottom",
      content:
        "Cabeçalho: avatar, nome, chips de identificação e botões — Editar cadastro, Nova Solicitação (relacionamento) e Nova Compra (comercial), quando aplicável.",
    },
    {
      target: ".tour-client-detail-kpis",
      placement: "top",
      content:
        "Indicadores rápidos: valores agregados de compras e relacionamento ajudam a contextualizar antes de atender ou negociar.",
    },
  ];
}

function productsPageSteps() {
  return [
    {
      target: ".tour-products-root",
      placement: "top",
      content:
        "Produtos: catálogo do e-commerce ou operação — listagem, importações e edição em massa conforme permissões.",
    },
    {
      target: ".tour-products-header",
      placement: "bottom",
      content:
        "Barra de ações: importação, colagem, exportação, atualização de estoque/preços e botão de novo produto. Cada ícone corresponde a um fluxo na ferramenta.",
    },
    {
      target: ".tour-products-table",
      placement: "top",
      content:
        "Grade de produtos: busca, filtros e paginação; clique em uma linha para editar no formulário lateral ou modal.",
    },
  ];
}

function pipelinePageSteps() {
  return [
    {
      target: ".tour-pipeline-root",
      placement: "top",
      content:
        "Níveis de consciência: quadro tipo Kanban com clientes posicionados por etapa da jornada. Arraste cartões entre colunas para atualizar o estágio (quando permitido).",
    },
    {
      target: ".tour-pipeline-board",
      placement: "top",
      content:
        "Colunas: cada coluna é um nível cadastrado. Dentro dela, os cards são clientes nesse estágio. Role horizontalmente se houver muitas etapas.",
    },
  ];
}

function salesPipelinePageSteps() {
  return [
    {
      target: ".tour-sales-pipeline-root",
      placement: "top",
      content:
        "Pipeline de vendas: funil comercial ligado aos tipos e subtipos de solicitação. Breadcrumbs indicam o nível de drill-down nas etapas.",
    },
    {
      target: ".tour-sales-pipeline-toolbar",
      placement: "bottom",
      content:
        "Controles: alternar visual compacta, atualizar o quadro manualmente e criar nova solicitação comercial sem sair da tela.",
    },
    {
      target: ".tour-sales-pipeline-board",
      placement: "top",
      content:
        "Board: colunas são etapas do funil; cards representam oportunidades ou pedidos. Clique nos títulos das etapas para navegar em subníveis, quando existirem.",
    },
  ];
}

function agentFormPageSteps() {
  return [
    {
      target: ".tour-agent-form",
      placement: "top",
      content:
        "Configuração do agente: defina nome, uma função (Recepcionista, FAQ ou passagens), documentos/links só no FAQ, e parâmetros avançados. Salve para publicar alterações no atendimento automatizado.",
    },
  ];
}

function agentAnalyticsPageSteps() {
  return [
    {
      target: ".tour-agent-analytics",
      placement: "top",
      content:
        "Estatísticas do agente: volume de interações, tempo médio de resposta e uso por função — útil para ajustar prompts e conteúdo de FAQ.",
    },
  ];
}

function agentsPageSteps() {
  return [
    {
      target: ".tour-agents-root",
      placement: "top",
      content:
        "Agentes de IA: lista dos assistentes configurados no workspace (FAQ, recepção, venda de passagens/BPe, etc.).",
    },
    {
      target: ".tour-agents-header",
      placement: "bottom",
      content:
        "Criar agente abre o assistente de configuração. A tabela abaixo concentra busca, edição e exclusão.",
    },
    {
      target: ".tour-agents-table",
      placement: "top",
      content:
        "Tabela de agentes: clique na linha para editar; use exportação ou exclusão em lote se sua permissão permitir.",
    },
  ];
}

function searchResultsPageSteps() {
  return [
    {
      target: ".tour-search-results-root",
      placement: "top",
      content:
        "Resultados da busca: tela dedicada à pesquisa avançada em solicitações e clientes, com URL compartilhável.",
    },
    {
      target: ".tour-search-results-toolbar",
      placement: "bottom",
      content:
        "Campo de termo e filtros rápidos: refine a consulta e abra o modal de filtros avançados (datas, tipos, canais, responsáveis).",
    },
    {
      target: ".tour-search-results-tabs",
      placement: "top",
      content:
        "Abas separam tipos de resultado (por exemplo relacionamento vs comercial vs clientes). O conteúdo muda conforme a aba ativa.",
    },
  ];
}

function profilePageSteps() {
  return [
    {
      target: ".tour-profile-page",
      placement: "top",
      content:
        "Meu perfil: dados pessoais, foto, preferências de tema, opções de assinatura em mensagens e segurança. Salve após alterar.",
    },
  ];
}

function settingsPageSteps(pathname) {
  const title = settingsTitleFromPath(pathname);
  return [
    {
      target: ".tour-main-content",
      placement: "top",
      content: `${title} (Configurações): tela administrativa do projeto. Em geral há listagem com busca, inclusão e edição. Alterações podem afetar todos os usuários — confira antes de salvar. Materiais específicos do workspace ficam em Ajuda e Treinamento (menu do avatar).`,
    },
  ];
}

function catalogPageSteps(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  const seg = p.split("/").filter(Boolean)[0] || "";
  const labels = {
    categories: "Categorias",
    coupons: "Cupons",
    banners: "Banners",
    contents: "Conteúdos",
    "pipeline-stages": "Etapas de pipeline",
    "awareness-stages": "Etapas de consciência",
    surveys: "Pesquisas",
    campaigns: "Campanhas",
    "user-details": "Detalhes de usuário",
    "user-plan": "Plano do usuário",
  };
  const title = labels[seg] || seg.replace(/-/g, " ") || "Cadastros";

  return [
    {
      target: ".tour-main-content",
      placement: "top",
      content: `Esta tela (${title}) faz parte dos cadastros ou catálogos do workspace. Use listagem e formulários para manter dados alinhados ao projeto atual.`,
    },
  ];
}

function genericPageSteps() {
  return [
    {
      target: ".tour-main-content",
      placement: "top",
      content:
        "Nesta rota, o conteúdo específico aparece nesta área. Explore o menu lateral para outros módulos; materiais do workspace estão em Ajuda e Treinamento no menu do avatar.",
    },
  ];
}

export function getPageTourSteps(routeKey, pathname, isMobile) {
  switch (routeKey) {
    case "chat":
      return chatPageSteps(isMobile);
    case "dashboard":
      return dashboardPageSteps();
    case "clients":
      return clientsPageSteps();
    case "client-detail":
      return clientDetailPageSteps();
    case "products":
      return productsPageSteps();
    case "pipeline":
      return pipelinePageSteps();
    case "sales-pipeline":
      return salesPipelinePageSteps();
    case "agents":
      return agentsPageSteps();
    case "agent-form":
      return agentFormPageSteps();
    case "agent-analytics":
      return agentAnalyticsPageSteps();
    case "search-results":
      return searchResultsPageSteps();
    case "profile":
      return profilePageSteps();
    case "settings":
      return settingsPageSteps(pathname);
    case "catalog":
      return catalogPageSteps(pathname);
    case "generic":
    default:
      return genericPageSteps();
  }
}
